import { send } from "../../fetch";
import { errorConstants } from "../../services/PurchaseErrorMessages";
import { CustomerDetailsProto } from "@noom/noom-contracts/noom_contracts/billing/bms/common/shared";
import { OrderProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/orders_service";
import { getCountryCode, getLanguage, getRouteId } from "../../meristemContext";
import { BillingAddress } from "src/utils/redux/slices/checkout";
import { AddressProto } from "@noom/noom-contracts/noom_contracts/billing/address/address";
import { BillingUserProto } from "@noom/noom-contracts/noom_contracts/billing/user/billing_user";
import { PaymentMethodProto } from "@noom/noom-contracts/noom_contracts/billing/payment_method/payment_method";
import { appConfig } from "src/config";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { PurchaseProgramResponse } from "./checkout";
import getStore from "src/utils/redux/store";
import { getAuthorizationHeader } from "src/api/utils";
import {
  ExecutePurchaseOrderRequestProto_Body,
  ExecutePurchaseOrderResponseProto,
} from "@noom/noom-contracts/noom_contracts/growth/orders/growth_orders_service";
import { OrderOperationProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/order_operation";
import { getActiveUser } from "@noom/hive-auth";
import { logDebug } from "src/utils/monitoring/logging";
import type { PreviewBillingCyclesResponseProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/bms_subscription_service";
import { InvoiceProto } from "@noom/noom-contracts/noom_contracts/billing/bms/invoices/invoice";

export class PurchaseOrderError extends Error {
  constructor(public readonly code: string) {
    super(code);
  }
}

// TODO: Remove the details JSON parsing once the backend response is refactored (MGEXP-3496).
function extractErrorCode(responseData: any): string {
  if (responseData?.errorType) {
    return responseData.errorType;
  }
  if (typeof responseData?.details === "string") {
    const bodyMatch = responseData.details.match(
      /HTTP response body:\s*(\{.*\})/
    );
    if (bodyMatch) {
      try {
        const parsed = JSON.parse(bodyMatch[1]);
        if (parsed.errorCode) {
          return parsed.errorCode;
        }
      } catch {
        // ignore parse errors
      }
    }
  }
  return errorConstants.errorUnknown;
}

/**
 * Creates an order for med checkout flow
 */
export async function createOrderRequest(
  operation?: OrderOperationProto,
  accessCode?: string,
  previewMode = false,
  customerDetails?: CustomerDetailsProto
): Promise<OrderProto> {
  if (!operation) {
    throw new Error("Operation is required");
  }
  if (!previewMode && !accessCode) {
    throw new Error("Access code is required when previewMode is false");
  }
  let apiUrl = `${appConfig.BILLING_CORE_API_URL}/billing/v2/orders`;
  if (previewMode) {
    apiUrl += `?validateOnly=true`;
  }
  const task = trackTask("CreateOrderRequest", {
    previewMode,
    hasCustomerDetails: Boolean(customerDetails),
  });
  task.start();
  try {
    const request: any = {
      operations: [operation],
      customerDetails,
      accessCode,
    };
    const response = await send("POST", apiUrl, request, {
      headers: getAuthorizationHeader(),
    });
    task.success();
    return response;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

/**
 * Gets an existing order by UUID
 */
export async function getOrderRequest(params: {
  orderUuid: string;
}): Promise<OrderProto> {
  const task = trackTask("GetOrderRequest", params);
  task.start();
  try {
    const response = await send(
      "GET",
      `${appConfig.BILLING_CORE_API_URL}/billing/v2/orders/${params.orderUuid}`,
      null,
      {
        headers: getAuthorizationHeader(),
      }
    );
    task.success();
    return response;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

export function formatAddressProto(
  address?: BillingAddress,
  recipient?: string
) {
  if (!address) {
    return undefined;
  }
  return {
    recipient: recipient || address.name || "",
    addressLine1: address.address1 || "",
    addressLine2: address.address2,
    city: address.city || "",
    zoneCode: address.region || "",
    postalCode: address.zipcode || "",
    countryCode: address.country || getCountryCode(),
  };
}

/**
 * Creates an address in BCI and returns the created AddressProto
 */
export async function createAddress(params: {
  accessCode: string;
  address?: BillingAddress;
  recipient?: string;
}): Promise<AddressProto> {
  const task = trackTask("CreateBillingAddress", {});
  task.start();
  try {
    const createAddressRequest: AddressProto = formatAddressProto(
      params.address,
      params.recipient
    );

    const address: AddressProto = await send(
      "POST",
      `${appConfig.BILLING_CUSTOMERINFO_API_URL}/billing/v2/users/${params.accessCode}/addresses`,
      createAddressRequest,
      {
        headers: getAuthorizationHeader(),
      }
    );
    task.success();
    return address;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

/**
 * Updates the billing user active payment method in BCI
 */
export async function updateBillingUserRequest({
  accessCode,
  activePaymentMethod,
  preferredShippingAddressId,
  homeAddressId,
}: {
  accessCode: string;
  activePaymentMethod?: Partial<PaymentMethodProto>;
  preferredShippingAddressId?: string;
  homeAddressId?: string;
}): Promise<BillingUserProto | undefined> {
  if (!activePaymentMethod && !preferredShippingAddressId && !homeAddressId) {
    logDebug("UpdateBillingUser: Skipping update");
    return undefined;
  }
  const request: Partial<BillingUserProto> = {
    preferredShippingAddressId,
    homeAddressId,
    activePaymentMethod: activePaymentMethod as PaymentMethodProto,
  };
  const updateMask = [
    activePaymentMethod ? "activePaymentMethod" : undefined,
    preferredShippingAddressId ? "preferredShippingAddressId" : undefined,
    homeAddressId ? "homeAddressId" : undefined,
  ]
    .filter(Boolean)
    .join(",");
  const response = await send<BillingUserProto>(
    "PATCH",
    `${appConfig.BILLING_CUSTOMERINFO_API_URL}/billing/v2/users/${accessCode}?updateMask=${updateMask}`,
    request,
    {
      headers: getAuthorizationHeader(),
    }
  );
  return response;
}

export async function executePurchaseOrder(
  orderUuid: string,
  recaptchaToken: string
): Promise<PurchaseProgramResponse> {
  const task = trackTask("ExecutePurchaseOrder", {
    orderUuid,
  });
  task.start();
  try {
    const { userData, checkoutOrders } = getStore().getState();
    const url = "/api/orders/executePurchaseOrder/";
    // Optional ML eLTV (med flows). Forwarded onto the Iterable signed_up event by growth.
    const mlEltvValue = checkoutOrders.prefetchedEltv?.eltvValue;
    const body: ExecutePurchaseOrderRequestProto_Body = {
      orderUuid,
      recaptchaToken,
      routeId: getRouteId(),
      language: getLanguage(),
      ...(mlEltvValue != null && { mlEltvValue }),
    };
    const response = await send<ExecutePurchaseOrderResponseProto>(
      "POST",
      url,
      body,
      {
        headers: getAuthorizationHeader(),
      }
    );
    const { email, name } = getActiveUser();
    task.success();
    return {
      name,
      email,
      payment_cc_last_4:
        checkoutOrders?.billingUser?.activePaymentMethod?.last4,
      payment_cc_type: checkoutOrders?.billingUser?.activePaymentMethod?.type,
      user_id: userData.user_id,
      eltv_13_months: response.eltvValue,
      legacyPlanName: response.legacyPlanName,
    } as PurchaseProgramResponse;
  } catch (ex) {
    const errorType = extractErrorCode(ex?.responseData);
    task.failed(ex, { error: errorType });
    throw new PurchaseOrderError(errorType);
  }
}

/**
 * Executes an order directly via billing-core
 * Used for switch orders that don't require payment processing
 */
export async function executeOrderRequest(
  orderId: string
): Promise<OrderProto> {
  const task = trackTask("ExecuteOrder", {
    orderId,
  });
  task.start();
  try {
    const response = await send<OrderProto>(
      "POST",
      `${appConfig.BILLING_CORE_API_URL}/billing/v2/orders/${orderId}/execute`
    );
    task.success();
    return response;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

/**
 * Gets the existing billing user by access code
 */
export async function getBillingUserRequest(
  accessCode: string
): Promise<BillingUserProto> {
  const task = trackTask("GetBillingUserRequest", { accessCode });
  task.start();
  try {
    const response = await send(
      "GET",
      `${appConfig.BILLING_CUSTOMERINFO_API_URL}/billing/v2/users/${accessCode}`
    );
    task.success();
    return response;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

export async function getAddressRequest(
  accessCode: string,
  addressId: string
): Promise<AddressProto> {
  const task = trackTask("GetAddressRequest", { accessCode, addressId });
  task.start();
  try {
    const response = await send(
      "GET",
      `${appConfig.BILLING_CUSTOMERINFO_API_URL}/billing/v2/users/${accessCode}/addresses/${addressId}`
    );
    task.success();
    return response;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

/**
 * Previews billing cycles for an existing subscription
 */
export async function fetchPreviewBillingCycles(
  subscriptionId: string
): Promise<PreviewBillingCyclesResponseProto> {
  const task = trackTask("FetchPreviewBillingCycles", { subscriptionId });
  task.start();
  try {
    const response = await send<PreviewBillingCyclesResponseProto>(
      "POST",
      `${appConfig.BILLING_CORE_API_URL}/bms/v1/subscription/previewBillingCycles`,
      { subscriptionId }
    );
    task.success();
    return response;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

export async function fetchInvoice(invoiceId: string): Promise<InvoiceProto> {
  const task = trackTask("FetchInvoice", { invoiceId });
  task.start();
  try {
    const result = await send<InvoiceProto>(
      "GET",
      `${appConfig.BILLING_CORE_API_URL}/billing/v2/invoices/${invoiceId}`
    );
    task.success();
    return result;
  } catch (error) {
    task.failed(error);
    throw error;
  }
}
